import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_inbound_ip_address_policies'

const URLS = {
  CAMPUS: 'https://campus.barracuda.com/doc/96023004/'
}

function InboundIpAddressPoliciesI18n({
  classes,
  productName
}: {
  classes: ReturnType<typeof styles>
  productName?: string
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  return (
    <div className={classes.content} data-testid="content">
      {formatMessage('note', { productName })}
      <br />
      <br />
      <h3>{formatMessage('ip_blocking_exemption.title')}</h3>
      <div>
        {formatMessage('ip_blocking_exemption.description')}
        <br />
        <br />
        {formatMessage('ip_blocking_exemption.important_prefix', { productName })}
        <a href={URLS.CAMPUS} target="_blank" rel="noreferrer">
          {formatMessage('ip_blocking_exemption.important_link')}
        </a>
        {formatMessage('ip_blocking_exemption.important_suffix')}
        <br />
        <br />
        {formatMessage('ip_blocking_exemption.precedence')}
        <br />
        <br />
        {formatMessage('ip_blocking_exemption.sorting')}
        <ul>
          <li>{formatMessage('ip_blocking_exemption.columns.ip_address')}</li>
          <li>{formatMessage('ip_blocking_exemption.columns.netmask')}</li>
          <li>{formatMessage('ip_blocking_exemption.columns.policy')}</li>
          <li>{formatMessage('ip_blocking_exemption.columns.comment')}</li>
        </ul>
      </div>
      <h3>{formatMessage('trusted_forwarders.title')}</h3>
      {formatMessage('trusted_forwarders.description', { productName })}
      <br />
      <br />
      {formatMessage('trusted_forwarders.sorting')}
      <ul>
        <li>{formatMessage('trusted_forwarders.columns.ip_address')}</li>
        <li>{formatMessage('trusted_forwarders.columns.netmask')}</li>
        <li>{formatMessage('trusted_forwarders.columns.comment')}</li>
      </ul>
    </div>
  )
}

function InboundIpAddressPolicies(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <InboundIpAddressPoliciesI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <b>Note</b>: These settings apply to all domains you have verified in {props.productName} for processing email,
      unless you change these settings for a specific domain. In that case, the domain-level settings override the
      global system settings.
      <br />
      <br />
      <h3>IP Blocking / Exemption</h3>
      <div>
        Add any IP addresses or networks to always block or always exempt (allow) here. Use a netmask of 255.255.255.255
        to add an individual IP address. Allowed IP addresses/networks bypass spam scoring (see <b>Enable Cloudscan</b>{' '}
        on the <b>Inbound Settings &gt; Anti-spam/Antivirus</b> page) as well as all other block lists. Virus scanning
        still applies.
        <br />
        <br />
        <b>Important</b>: When entering an IP range in {props.productName}, it is <i>critical</i> that the starting IP
        address is valid. For more information, see{' '}
        <a href="https://campus.barracuda.com/doc/96023004/" target="_blank" rel="noreferrer">
          {' '}
          Barracuda Campus.
        </a>
        <br />
        <br />
        This list of IP addresses that you choose to block takes precedence over the Barracuda Reputation Block List and
        Custom RBL entries.
        <br />
        <br />
        Click on one of the following column headings to sort the <b>IP Blocking / Exemption</b> table:
        <ul>
          <li>IP Address</li>
          <li>Netmask</li>
          <li>Policy</li>
          <li>Comment</li>
        </ul>
      </div>
      <h3>Trusted Forwarders</h3>
      Add IP addresses of machines that you have set up to forward email (that is, Trusted Forwarders) to{' '}
      {props.productName} from outside sources. {props.productName} exempts any IP address in this list from Rate
      Control, Sender Policy Framework (SPF) checks, and IP Reputation (see the{' '}
      <b>Inbound Settings &gt; Anti-Spam/Antivirus</b> page). In the Received headers, {props.productName} continues
      looking beyond a Trusted Forwarder IP address until it encounters the first non-trusted IP address. At this point,
      Rate Control, SPF checks, and IP Reputation checks are applied.
      <br />
      <br />
      Click on one of the following column headings to sort the <b>Trusted Forwarder</b> table:
      <ul>
        <li>IP Address</li>
        <li>Netmask</li>
        <li>Comment</li>
      </ul>
    </div>
  )
}

export default InboundIpAddressPolicies
